import type { OrderTrendPoint } from "@/components/Organisms/OrderTrendChart/OrderTrendChart.types";
import type { DashboardOrder } from "@/lib/validation/dashboard.schemas";

const PERSIAN_WEEKDAY_LABELS: Record<number, string> = {
  6: "شنبه",
  0: "یک",
  1: "دو",
  2: "سه",
  3: "چهار",
  4: "پنج",
  5: "جمعه",
};

function startOfDay(date: Date): Date {
  const copy = new Date(date);
  copy.setHours(0, 0, 0, 0);
  return copy;
}

function dayKey(date: Date): string {
  return startOfDay(date).toISOString().slice(0, 10);
}

function getOrderCreatedAt(order: DashboardOrder): Date | null {
  const raw = order.created_at ?? order.createdAt;
  if (!raw) return null;

  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? null : startOfDay(parsed);
}

function getLastSevenDays(from: Date): Date[] {
  const today = startOfDay(from);
  const days: Date[] = [];

  for (let offset = 6; offset >= 0; offset -= 1) {
    const day = new Date(today);
    day.setDate(today.getDate() - offset);
    days.push(day);
  }

  return days;
}

export function buildWeeklyOrderTrend(
  orders: DashboardOrder[],
  referenceDate: Date = new Date(),
): OrderTrendPoint[] {
  const days = getLastSevenDays(referenceDate);
  const totals = new Map(days.map((day) => [dayKey(day), 0]));

  for (const order of orders) {
    const createdAt = getOrderCreatedAt(order);
    if (!createdAt) continue;

    const key = dayKey(createdAt);
    if (!totals.has(key)) continue;

    totals.set(key, (totals.get(key) ?? 0) + (order.final_payable_amount ?? 0));
  }

  const values = days.map((day) => totals.get(dayKey(day)) ?? 0);
  const maxValue = Math.max(...values, 0);
  const maxIndex = values.indexOf(maxValue);

  return days.map((day, index) => ({
    label: PERSIAN_WEEKDAY_LABELS[day.getDay()],
    value: values[index],
    highlight: maxValue > 0 && index === maxIndex,
  }));
}

export function computeWeekOverWeekGrowth(
  orders: DashboardOrder[],
  referenceDate: Date = new Date(),
): number | undefined {
  const today = startOfDay(referenceDate);
  let thisWeekTotal = 0;
  let lastWeekTotal = 0;

  for (const order of orders) {
    const createdAt = getOrderCreatedAt(order);
    if (!createdAt) continue;

    const diffDays = Math.floor((today.getTime() - createdAt.getTime()) / 86_400_000);
    const amount = order.final_payable_amount ?? 0;

    if (diffDays >= 0 && diffDays < 7) {
      thisWeekTotal += amount;
    } else if (diffDays >= 7 && diffDays < 14) {
      lastWeekTotal += amount;
    }
  }

  if (lastWeekTotal === 0) return undefined;

  return Math.round(((thisWeekTotal - lastWeekTotal) / lastWeekTotal) * 100);
}
